import axios from "axios";
import { X_API_KEY } from "../constants";
import IdentityClient from "./IdentityClient";
import { Cache } from "../util/Cache.util";

export class LiveApiCache {
	static #AUTH_EXPIRATION_SECS = 60 * 60 * 24 * 60; // match Live's "auth" expiration
	/** @type Promise */
	static SharedTokenFetch;
	
	static Set(token) {
		if (!token) {
			return;
		}
		Cache.Local().set("api-token", { token, expires_on: this.#Get_Expiration() });
	}

	static Get() {
		const stored = Cache.Local().get("api-token");
		if (stored?.expires_on > Date.now()) {
			return stored.token;
		}
		LiveApiCache.Reset();
	}

	static Reset() {
		Cache.Local().set("api-token", undefined);
	}

	static #Get_Expiration() {
		return Date.now() + (this.#AUTH_EXPIRATION_SECS * 1000);
	}
}

const LiveApiClient = axios.create({
	baseURL: process.env.REACT_APP_API_CLIENT_BASE_URL || "https://staging.bosslogics.live/api",
	headers: {
		Authorization: "",
		Project: "together.boss",
		Accept: "application/json",
		'Cache-Control': 'no-cache',
		// 'Pragma': 'no-cache',
		'Expires': '0',
	},
});

LiveApiClient.interceptors.request.use(
	async config => {
		const savedToken = LiveApiCache.Get();
		
		const checkToken = token => {
			const base64Url = token.split('.').at(1);
			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
			const jsonPayload = decodeURIComponent(window.atob(base64).split('')
				.map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
			if (!jsonPayload) {
				return false;
			}
			
			try {
				const payload = JSON.parse(jsonPayload);
				return !(payload.exp && payload.exp * 1000 < Date.now());
			} catch (e) {
				return false;
			}
		}
		
		if (savedToken && checkToken(savedToken)) {
			config.headers.Authorization = savedToken || '';
			return config;
		}

		if (X_API_KEY) {
			config.headers['x-api-key'] = X_API_KEY;
			return config;
		}

		LiveApiCache.SharedTokenFetch ??= IdentityClient.post("getToken");
		
		const { data: { token } } = await LiveApiCache.SharedTokenFetch;
		LiveApiCache.SharedTokenFetch = undefined;
		config.headers.Authorization = token;
		LiveApiCache.Set(token);

		Object.keys(config.headers).forEach(key => {
			if (config.headers[key] === undefined) {
				delete config.headers[key];
			}
		});

		return config;
	},
	error => Promise.reject(error)
);

LiveApiClient.interceptors.response.use(
	response => response,
	error => {
		if (error.response?.status !== 401) {
			return Promise.reject(error);
		}
		
		// dont error on prefetch
		if (error.config.method !== "options") {
			const errors = error.response.data.errors;
			const no_auth_header = errors.find(it => it.descr?.toLowerCase().startsWith("no authorization header supplied"));
			
			/*
				if response failed because there's no auth header,
				something's wrong on the front end and token cookie and sessionStorage-stored token are gone
				and login is required to get it back anyway
			 */
			if (no_auth_header) {
				localStorage.removeItem('catalog_invitation:data');
				localStorage.removeItem("organization_member:data");
				localStorage.removeItem("currentUser");
				window.location = '/login?redirect=' + window.location.pathname;
			}
		}
		return Promise.reject(error);
	}
);

export default LiveApiClient;
